import { useCallback, useMemo, useState } from "react";
import {
  useDelete,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  TextField,
  ReferenceField,
  FunctionField,
  SimpleShowLayout,
  type RaRecord,
} from "react-admin";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncIcon from "@mui/icons-material/Sync";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { ChangeSadaratDialog } from "@/components/raza/ChangeSadaratDialog";
import { getApiUrl, slotNameMap } from "@/constants";
import httpClient from "@/dataprovider/http-client";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import { formatListDate } from "@/utils/date-format";
import { eventNeedsSadaratAllocation } from "@/utils/event-needs-sadarat-allocation";
import { eventNeedsAnySadarat } from "@/utils/format-raza-event-sadarat-display";
import { razaEventCalendarBaseTitle } from "@/utils/raza-backed-event-calendar-title";
import { hasAnyPermission, hasPermission } from "@/utils/permission-utils";
import RazaRequestBookingSummary from "./RazaRequestBookingSummary";
import RazaRequestLagatSection from "./RazaRequestLagatSection";
import RazaRequestCreateHallSlotDialog from "./RazaRequestCreateHallSlotDialog";
import RazaRequestLinkHallSlotDialog from "./RazaRequestLinkHallSlotDialog";
import { hallSlotMatchesRazaEventDate } from "@/utils/hall-slot-matches-raza-event-date";
import {
  RazaRequestEventLagatBlock,
  type RazaEventLagatSummary,
} from "./components/RazaRequestEventLagatBlock";
import RazaSafaiShowField from "./components/RazaSafaiShowField";
import {
  RazaRequestRazaEventCreateDialog,
  RazaRequestRazaEventEditDialog,
} from "./RazaRequestRazaEventDialogs";

type RazaEventHallLine = {
  id: string;
  date: string | Date;
  slot: string;
  hallName: string | null;
  hallShortCode: string | null;
};

type RazaEventWorkspaceRow = {
  id: string;
  date: string | Date;
  startTime?: string | null;
  title: string | null;
  purpose?: string | null;
  /** Resolved booking purpose name for `ev.purpose` id (when available). */
  purposeName?: string | null;
  hostItsNo?: string | null;
  hostName?: string | null;
  sadaratName: string | null;
  sadaratMaleName?: string | null;
  sadaratFemaleName?: string | null;
  sadaratMaleId?: string | null;
  sadaratFemaleId?: string | null;
  needSadaratMale?: boolean;
  needSadaratFemale?: boolean;
  /** @deprecated legacy single flag — prefer needSadaratMale/Female */
  needSadarat?: boolean;
  googleCalendarLinked: boolean;
  hallCount: number;
  hallBookings?: RazaEventHallLine[];
  lagatReceipt?: RazaEventLagatSummary | null;
};

type BookingSummary = {
  id: string;
  bookingNo: string | null;
};

type RazaWorkspaceRecord = RaRecord & {
  razaEvents?: RazaEventWorkspaceRow[];
  booking?: BookingSummary | null;
};

function slotLabel(slot: string): string {
  return slotNameMap[slot] ?? slot;
}

type RazaRequestWorkspaceProps = {
  section?: "request" | "hall-events";
};

const RazaRequestWorkspace = ({ section = "request" }: RazaRequestWorkspaceProps) => {
  const record = useRecordContext<RazaWorkspaceRecord>();
  const refresh = useRefresh();
  const notify = useNotify();
  const [deleteOne, { isLoading: deletingEvent }] = useDelete();
  const { permissions } = usePermissions();

  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteEventConfirm, setDeleteEventConfirm] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [linkHallEventId, setLinkHallEventId] = useState<string | null>(null);
  const [createHallEventId, setCreateHallEventId] = useState<string | null>(null);
  const [allocateSadaratEventId, setAllocateSadaratEventId] = useState<string | null>(null);
  const [unlinkingHallId, setUnlinkingHallId] = useState<string | null>(null);

  const razaRequestId = record?.id != null ? String(record.id) : "";
  const events: RazaEventWorkspaceRow[] = useMemo(
    () => (record?.razaEvents as RazaEventWorkspaceRow[] | undefined) ?? [],
    [record?.razaEvents]
  );
  const booking = record?.booking;

  const canViewEvents = hasPermission(permissions, "razaEvents.view");
  const canCreateEvent = hasPermission(permissions, "razaEvents.create");
  const canEditEvent = hasPermission(permissions, "razaEvents.edit");
  const canDeleteEvent = hasPermission(permissions, "razaEvents.delete");
  const canSyncCalendar = hasAnyPermission(permissions, ["razaEvents.create", "razaEvents.edit"]);
  const canEditBookings = hasPermission(permissions, "bookings.edit");

  const openEdit = useCallback((id: string) => {
    setEditId(id);
    setEditOpen(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setEditId(null);
  }, []);

  const handleSync = useCallback(
    async (eventId: string) => {
      try {
        await httpClient(`${getApiUrl()}/raza-events/${eventId}/sync-calendar`, {
          method: "POST",
          body: "{}",
        });
        notify("Calendar updated", { type: "success" });
        refresh();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Calendar sync failed";
        notify(msg, { type: "error" });
      }
    },
    [notify, refresh]
  );

  const openDeleteEventConfirm = useCallback((eventId: string, displayLabel: string) => {
    const label = displayLabel.trim() || "this event";
    setDeleteEventConfirm({ id: eventId, label });
  }, []);

  const confirmDeleteEvent = useCallback(() => {
    if (!deleteEventConfirm) return;
    const eventId = deleteEventConfirm.id;
    deleteOne(
      "raza-events",
      { id: eventId, previousData: { id: eventId } },
      {
        onSuccess: () => {
          setDeleteEventConfirm(null);
          notify("Event deleted", { type: "info" });
          refresh();
        },
        onError: (err) => {
          notify(typeof err === "string" ? err : "Delete failed", { type: "error" });
        },
      }
    );
  }, [deleteEventConfirm, deleteOne, notify, refresh]);

  const handleUnlinkHall = useCallback(
    async (hallBookingId: string) => {
      setUnlinkingHallId(hallBookingId);
      try {
        await httpClient(`${getApiUrl()}/hallBookings/${hallBookingId}`, {
          method: "PUT",
          body: JSON.stringify({ razaEventId: null }),
        });
        notify("Hall slot unlinked from this event", { type: "info" });
        refresh();
      } catch (e: unknown) {
        notify(e instanceof Error ? e.message : "Unlink failed", { type: "error" });
      } finally {
        setUnlinkingHallId(null);
      }
    },
    [notify, refresh]
  );

  if (!record) return null;

  const linkEvent = events.find((e) => e.id === linkHallEventId);
  const createEvent = events.find((e) => e.id === createHallEventId);

  return (
    <Box sx={{ maxWidth: 1000 }}>
      {section === "request" ? (
        <>
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="h6" component="h2">
                Request details
              </Typography>
              <Button
                component={RouterLink}
                to={`/razaRequests/${razaRequestId}/edit`}
                size="small"
                variant="outlined"
              >
                Edit request
              </Button>
            </Stack>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <SimpleShowLayout>
                <TextField source="itsNo" label="ITS" />
                <TextField source="name" label="Name" />
                <TextField source="mohallah" label="Mohallah" />
                <ReferenceField source="purpose" reference="bookingPurpose" label="Purpose">
                  <TextField source="name" />
                </ReferenceField>
                <FunctionField
                  label="Request date"
                  render={(r: RaRecord) => formatListDate(r?.requestDate, { empty: "—" })}
                />
                <FunctionField
                  label="Raza granted on"
                  render={(r: RaRecord) => formatListDate(r?.razaGranted, { empty: "Pending" })}
                />
              </SimpleShowLayout>
              <RazaSafaiShowField />
            </Paper>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box>
            <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
              General lagat
            </Typography>
            <RazaRequestLagatSection variant="general" />
          </Box>
        </>
      ) : (
        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" component="h2">
              Hall & events
            </Typography>
            {canCreateEvent && razaRequestId ? (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                size="small"
                onClick={() => setCreateOpen(true)}
              >
                Add event
              </Button>
            ) : null}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Manage events here. Add a booking and hall slots only when the request needs a hall.
          </Typography>
          <RazaRequestBookingSummary />

          <Divider sx={{ my: 2 }} />

          {events.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No events yet.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {events.map((ev) => {
                const halls = ev.hallBookings ?? [];
                const needsSadaratAllocation = eventNeedsSadaratAllocation(ev);
                const needsAnySadarat = eventNeedsAnySadarat(ev);
                return (
                  <Paper
                    key={ev.id}
                    variant="outlined"
                    sx={{ p: 2, bgcolor: "background.default" }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "flex-start" }}
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                          {razaEventCalendarBaseTitle(
                            ev.title,
                            ev.hostName,
                            ev.hostItsNo,
                            ev.purposeName
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatListDate(ev.date, { empty: "—" })}
                          {ev.startTime
                            ? ` · ${formatRazaEventStartTimeLabel(ev.startTime)}`
                            : null}
                          {ev.sadaratName?.trim() ? ` · Sadarat: ${ev.sadaratName.trim()}` : null}
                          {needsSadaratAllocation ? " · No sadarat" : null}
                        </Typography>
                        {ev.purpose ? (
                          <Typography
                            component="div"
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            Purpose:{" "}
                            <ReferenceField
                              record={ev as unknown as RaRecord}
                              source="purpose"
                              reference="bookingPurpose"
                              link={false}
                            />
                          </Typography>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {needsAnySadarat ? (
                          <Chip
                            size="small"
                            label={
                              ev.googleCalendarLinked ? "Calendar synced" : "Calendar not synced"
                            }
                            color={ev.googleCalendarLinked ? "success" : "default"}
                            variant={ev.googleCalendarLinked ? "filled" : "outlined"}
                          />
                        ) : (
                          <Chip size="small" label="No calendar sync" variant="outlined" />
                        )}
                        {canViewEvents ? (
                          <Button
                            component={RouterLink}
                            to={`/raza-events/${ev.id}/show`}
                            size="small"
                            variant="outlined"
                          >
                            View
                          </Button>
                        ) : null}
                        {canEditEvent && needsSadaratAllocation ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => setAllocateSadaratEventId(ev.id)}
                          >
                            Allocate sadarat
                          </Button>
                        ) : null}
                        {canEditEvent ? (
                          <Button size="small" variant="outlined" onClick={() => openEdit(ev.id)}>
                            Edit
                          </Button>
                        ) : null}
                        {canSyncCalendar && needsAnySadarat ? (
                          <Tooltip title="Sync to Google Calendar">
                            <IconButton
                              size="small"
                              aria-label="Sync calendar"
                              onClick={() => void handleSync(ev.id)}
                            >
                              <SyncIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {canDeleteEvent ? (
                          <Tooltip title="Delete event">
                            <IconButton
                              size="small"
                              aria-label="Delete event"
                              onClick={() =>
                                openDeleteEventConfirm(
                                  ev.id,
                                  razaEventCalendarBaseTitle(
                                    ev.title,
                                    ev.hostName,
                                    ev.hostItsNo,
                                    ev.purposeName
                                  )
                                )
                              }
                              color="error"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={2}>
                      <RazaRequestEventLagatBlock
                        razaRequestId={razaRequestId}
                        record={record}
                        eventId={ev.id}
                        eventPurpose={ev.purpose ?? null}
                        lagat={ev.lagatReceipt}
                      />

                      {booking?.id || halls.length > 0 ? (
                        <Box>
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="wrap"
                            useFlexGap
                            columnGap={1}
                            rowGap={0.5}
                            sx={{ mb: 1 }}
                          >
                            <Typography variant="subtitle2" color="text.secondary">
                              Hall slots (optional)
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {booking?.id && canEditBookings ? (
                                <>
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon fontSize="small" />}
                                    variant="outlined"
                                    onClick={() => setCreateHallEventId(ev.id)}
                                  >
                                    Create hall slot
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<LinkIcon fontSize="small" />}
                                    onClick={() => setLinkHallEventId(ev.id)}
                                  >
                                    Link existing
                                  </Button>
                                </>
                              ) : null}
                            </Stack>
                          </Stack>
                          {halls.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              No hall slots are linked to this event yet.
                            </Typography>
                          ) : (
                            <Table size="small" sx={{ mb: 1 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Hall</TableCell>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Slot</TableCell>
                                  {canEditBookings ? (
                                    <TableCell align="right">Actions</TableCell>
                                  ) : null}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {halls.map((h) => {
                                  const dateMismatch = !hallSlotMatchesRazaEventDate(
                                    h.date,
                                    ev.date
                                  );
                                  return (
                                    <TableRow key={h.id}>
                                      <TableCell>
                                        {h.hallName?.trim() || h.hallShortCode?.trim() || "—"}
                                      </TableCell>
                                      <TableCell>
                                        <Stack spacing={0.25}>
                                          <Typography variant="body2" component="span">
                                            {formatListDate(h.date, { empty: "—" })}
                                          </Typography>
                                          {dateMismatch ? (
                                            <Typography variant="caption" color="warning.main">
                                              Date differs from event
                                            </Typography>
                                          ) : null}
                                        </Stack>
                                      </TableCell>
                                      <TableCell>{slotLabel(h.slot)}</TableCell>
                                      {canEditBookings ? (
                                        <TableCell align="right">
                                          <Tooltip title="Unlink from this event">
                                            <span>
                                              <IconButton
                                                size="small"
                                                aria-label="Unlink hall slot"
                                                color="warning"
                                                disabled={unlinkingHallId === h.id}
                                                onClick={() => void handleUnlinkHall(h.id)}
                                              >
                                                <LinkOffIcon fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                      ) : null}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>
      )}

      {linkHallEventId && booking?.id && linkEvent ? (
        <RazaRequestLinkHallSlotDialog
          open={true}
          onClose={() => setLinkHallEventId(null)}
          bookingId={booking.id}
          razaEventId={linkHallEventId}
          eventDate={linkEvent.date}
        />
      ) : null}

      {createHallEventId && booking?.id && createEvent ? (
        <RazaRequestCreateHallSlotDialog
          open={true}
          onClose={() => setCreateHallEventId(null)}
          bookingId={booking.id}
          razaEventId={createHallEventId}
          eventDate={createEvent.date}
          eventPurpose={createEvent.purpose ?? null}
        />
      ) : null}

      {createOpen && razaRequestId && record ? (
        <RazaRequestRazaEventCreateDialog
          onClose={() => setCreateOpen(false)}
          razaRequestId={razaRequestId}
          defaultHostItsNo={typeof record.itsNo === "string" ? record.itsNo : null}
          defaultHostName={typeof record.name === "string" ? record.name : null}
          defaultRequestPurpose={typeof record.purpose === "string" ? record.purpose : null}
        />
      ) : null}

      {editOpen && editId ? (
        <RazaRequestRazaEventEditDialog onClose={closeEdit} eventId={editId} />
      ) : null}

      {allocateSadaratEventId != null ? (
        <ChangeSadaratDialog
          open
          majlisId={allocateSadaratEventId}
          onClose={() => setAllocateSadaratEventId(null)}
        />
      ) : null}

      <Dialog
        open={deleteEventConfirm != null}
        onClose={() => {
          if (!deletingEvent) setDeleteEventConfirm(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete event</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {deleteEventConfirm?.label ?? "this event"}? This removes the event and its
            calendar entry.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEventConfirm(null)} disabled={deletingEvent}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteEvent}
            color="error"
            variant="contained"
            disabled={deletingEvent}
          >
            {deletingEvent ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RazaRequestWorkspace;
